import { i18next } from 'core';
import Loading from 'ui/atoms/Loading/Loading';
import * as coreEntities from 'core/store/index';
import { getLanguageByTenant } from 'core/utils/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { importLanguageResources } from '../utils/languages';

/**
 * TranslationsProvider
 *
 * Provider que carga las traducciones según el tenant del usuario.
 * Extrae la lógica de carga de traducciones desde los Routes para centralizarla a nivel de App.
 *
 * Funcionalidad:
 * - Lee el tenant y el idioma del usuario desde Redux
 * - Carga los recursos de traducciones basándose en el tenant usando importLanguageResources
 * - Inyecta las traducciones en i18next
 * - Cambia el idioma activo en i18next y Redux
 * - Muestra un loading mientras las traducciones se cargan
 */

interface TranslationsProviderProps {
  children: React.ReactNode;
}

function TranslationsProvider({ children }: TranslationsProviderProps): JSX.Element {
  const dispatch = useDispatch();
  const [translationsLoaded, setTranslationsLoaded] = useState<boolean>(false);

  // Selectores de Redux
  const tenant = useSelector((state: any) => coreEntities.organization.selectors.getTenant(state));
  const userLangId = useSelector((state: any) => state.auth?.user?.lang_id);

  useEffect(() => {
    if (tenant?.guid) {
      try {
        // 1. Obtener el idioma por defecto basado en el tenant y el idioma del usuario
        const defaultLanguage = getLanguageByTenant(tenant?.guid, userLangId, tenant?.lang_id);

        // 2. Importar los recursos de idiomas para el tenant
        const languageKeys = importLanguageResources(tenant, defaultLanguage);

        // 3. Añadir las traducciones al i18next
        Object.keys(languageKeys).forEach(function (lang) {
          // Verificar que existan traducciones para este idioma
          if (languageKeys[lang] && Array.isArray(languageKeys[lang])) {
            languageKeys[lang].forEach((langScope: any) => {
              Object.keys(langScope).forEach(function (langScopeNs) {
                i18next.addResources(lang, langScopeNs, langScope[langScopeNs]);
              });
            });
          } else {
            console.warn(`[TranslationsProvider] No translations found for language "${lang}", skipping.`);
          }
        });

        // 4. Cambiar el idioma activo en i18next
        i18next.changeLanguage(defaultLanguage);

        // 5. Actualizar el idioma en Redux
        dispatch(coreEntities.i18n.actions.setLanguage(defaultLanguage));

        // 6. Marcar las traducciones como cargadas
        setTranslationsLoaded(true);
      } catch (error) {
        console.error('Error loading translations:', error);
        // En caso de error, igualmente marcamos como cargado para no bloquear la app
        setTranslationsLoaded(true);
      }
    } else {
      dispatch(coreEntities.organization.actions.getOrganization());
    }
  }, [tenant?.guid, tenant?.lang_id, userLangId, dispatch]);

  // Si no hay tenant aún, no mostramos nada (esperamos a que se cargue)
  if (!tenant?.guid) {
    return <Loading />;
  }

  // Si las traducciones no están cargadas aún, mostramos loading
  if (!translationsLoaded) {
    return <Loading />;
  }

  // Renderizamos los children cuando las traducciones están cargadas
  return <>{children}</>;
}

export default TranslationsProvider;
